import React from "react";
import { alpha } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { CardBrandIcon } from "./CardBrandIcons";
import {
  getNewCardPaymentMethodParts,
  getSavedPaymentMethodParts,
  PaymentMethodDisplayParts,
  SavedBankAccountForLabel,
} from "./checkoutPaymentLabels";

type CheckoutReplacePaymentMethodDialogProps = {
  open: boolean;
  saving?: boolean;
  currentMethod: SavedBankAccountForLabel;
  newCardBrand: string;
  newCardLast4: string;
  onCancel: () => void;
  onConfirm: () => void;
};

const MethodIcon: React.FunctionComponent<{ parts: PaymentMethodDisplayParts }> = ({ parts }) => {
  if (parts.kind === "card") {
    return <CardBrandIcon brand={parts.brandForIcon ?? parts.title} />;
  }
  return (
    <Box
      sx={{
        width: 40,
        height: 26,
        borderRadius: 1,
        bgcolor: "grey.700",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AccountBalanceIcon sx={{ fontSize: 18, color: "common.white" }} />
    </Box>
  );
};

const PaymentMethodCard: React.FunctionComponent<{
  variant: "remove" | "save";
  parts: PaymentMethodDisplayParts;
}> = ({ variant, parts }) => {
  const isRemove = variant === "remove";

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        borderWidth: 2,
        borderStyle: isRemove ? "dashed" : "solid",
        borderColor: isRemove ? "grey.400" : "success.main",
        bgcolor: (theme) =>
          isRemove ? theme.palette.grey[50] : alpha(theme.palette.success.main, 0.1),
        opacity: isRemove ? 0.92 : 1,
      }}
    >
      <Stack spacing={1.5}>
        <Chip
          size="small"
          label={isRemove ? "Will be removed" : "Will be saved"}
          color={isRemove ? "default" : "success"}
          variant={isRemove ? "outlined" : "filled"}
          sx={{ alignSelf: "flex-start", fontWeight: 600, fontSize: 11 }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MethodIcon parts={parts} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2, mb: 0.25 }}>
              {parts.kind === "card" ? "Credit / debit card" : "ACH bank account"}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
              {parts.title}
            </Typography>
          </Box>
        </Stack>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "monospace",
            letterSpacing: 2,
            fontWeight: 600,
            color: isRemove ? "text.secondary" : "text.primary",
          }}
        >
          •••• {parts.last4}
        </Typography>
      </Stack>
    </Paper>
  );
};

export const CheckoutReplacePaymentMethodDialog: React.FunctionComponent<
  CheckoutReplacePaymentMethodDialogProps
> = ({
  open,
  saving = false,
  currentMethod,
  newCardBrand,
  newCardLast4,
  onCancel,
  onConfirm,
}) => {
  const currentParts = getSavedPaymentMethodParts(currentMethod);
  const newParts = getNewCardPaymentMethodParts(newCardBrand, newCardLast4);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "warning.light",
              color: "warning.dark",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SwapHorizIcon />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              Replace payment method?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Your saved method on file will change
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5, borderRadius: 1.5 }}>
          Saving this card replaces your current saved payment method. You can update it anytime
          in Bank Account settings.
        </Alert>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{ mb: 2 }}
        >
          <PaymentMethodCard variant="remove" parts={currentParts} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              color: "text.secondary",
            }}
          >
            <ArrowDownwardIcon sx={{ display: { xs: "block", sm: "none" } }} />
            <SwapHorizIcon sx={{ display: { xs: "none", sm: "block" } }} />
          </Box>

          <PaymentMethodCard variant="save" parts={newParts} />
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: "grey.100",
          }}
        >
          <CreditCardIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            After you confirm, your order will be placed using the new card.
          </Typography>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onCancel} disabled={saving} size="large" sx={{ minWidth: 100 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onConfirm}
          disabled={saving}
          size="large"
          sx={{ minWidth: 200, fontWeight: 600 }}
        >
          {saving ? "Saving…" : "Replace & confirm order"}
          {saving && <CircularProgress sx={{ ml: 1 }} size={16} color="inherit" />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutReplacePaymentMethodDialog;
